// Project Sentinel - API Service
import axios, { AxiosInstance, AxiosError } from 'axios';
import { withNomosSwitch } from './nomos/hook';
import { dropActiveSession } from './nomos/sessions';
import type { ReadPriority } from './nomos/batch';
import type {
  AssignBody, AssignResult, ConfirmResult, CountryResponsibleResult, FixListFormat, ProposeResult,
  QualityMetricsParams, QualityMetricsResponse, ResponsibleBody, ResponsibleCandidate,
} from '@/types/quality';
import type {
  WeeklyStatusEmailPreview, WeeklyStatusEmailSettings, WeeklyStatusEmailSettingsUpdate,
} from '@/types/weeklyStatusEmail';
import {
  Building,
  BuildingUpdate,
  BuildingListResponse,
  WeatherHistoryResponse,
  KPIResponse,
  TokenResponse,
  User,
  EnergySource,
  EnergySourceEmissionFactor,
  EnergyPrice,
  EnergyPriceCreate,
  SavingsInitiative,
  SavingsInitiativeCreate,
  InitiativeLifecycleBucket,
  InitiativeLifecycleCounts,
  InitiativeAggregates,
  CountryInitiativeCounts,
  EMPTY_INITIATIVE_COUNTS,
  EMPTY_INITIATIVE_AGGREGATES,
  UserListItem,
  AssigneeOption,
  MeterPoint,
  MeterPointUpdate,
  MeterLinkResult,
  MeterPointListResponse,
  BulkMeterPointListResponse,
  ConsumptionBreakdownResponse,
  ClimateZone,
  EnergyBenchmark,
  GridEmissionFactor,
  ClimateDataSummary,
  ConsumptionEstimate,
  BuildingTypeProfile,
  ConsumptionEstimateRequest,
  // Integration types
  ShellyCredentialsConnectionCreate,
  ShellyConnection,
  ShellyUnitsResponse,
  ShellyLinkUnitsRequest,
  ShellyLinkUnitsResponse,
  ShellySyncRequest,
  ShellySyncJob,
  // Google Places
  PlacesAutocompleteResponse,
  PlaceDetailsResponse,
  // Elkraft integration (Swedish energy via Kiona)
  ElkraftValidateRequest,
  ElkraftValidateResponse,
  ElkraftConnectionCreate,
  ElkraftConnection,
  ElkraftConnectionListResponse,
  ElkraftDiscoverResponse,
  ElkraftHierarchyNode,
  ElkraftBulkLinkRequest,
  ElkraftBulkLinkResponse,
  ElkraftSyncRequest,
  ElkraftSyncJob,
  // DataDis integration (Spanish electricity)
  DatadisValidateRequest,
  DatadisValidateResponse,
  DatadisConnectionCreate,
  DatadisConnectionResponse,
  DatadisDiscoverResponse,
  DatadisLinkRequest,
  DatadisBulkLinkRequest,
  DatadisBulkLinkResponse,
  DatadisSyncRequest,
  DatadisSyncJob,
  EforsyningUtilitiesResponse,
  EforsyningValidateResponse,
  EforsyningConnectionCreate,
  EforsyningConnectionResponse,
  EforsyningDiscoverResponse,
  EforsyningLinkRequest,
  EforsyningSyncRequest,
  EforsyningSyncJob,
  NoovaValidateResponse,
  NoovaConnectionCreate,
  NoovaConnectionResponse,
  NoovaDiscoveryReport,
  NoovaLinkResult,
  // KMD MinForsyning
  KmdValidateRequest,
  KmdValidateResponse,
  KmdConnectionCreate,
  KmdConnectionResponse,
  KmdDiscoverResponse,
  KmdLinkRequest,
  KmdSyncRequest,
  KmdSyncJob,
  KmdPlantsResponse,
  // Assets
  Asset,
  AssetCreate,
  AssetUpdate,
  AssetListResponse,
  AssetQuery,
  GroupCounts,
  AssetType,
  AssetFieldDefinitions,
  AssetImportResult,
  // Physical Tasks (FO-4)
  PhysicalTask,
  PhysicalTaskCreate,
  PhysicalTaskQuery,
  PhysicalTaskListResponse,
  // Field-ops records: comments, attachments, links (FO-4 Task 6)
  CommentRecord,
  AttachmentMeta,
  LinkRecord,
  LinkActionResponse,
  // Triage + Inbox (FO-6b, SEN-297)
  ListPage,
  TriageItem,
  TriageItemDetail,
  TriageAcceptRequest,
  TriageAcceptResponse,
  TriageMergeResponse,
  Notification,
  // Field-ops contacts, field links, visits (FO-5 Task 7)
  Contact,
  FieldLink,
  FieldLinkIssued,
  Visit,
  CustomerSettings,
  FiscalYear,
  BiddingZone,
  PriceResponse,
  CarbonIntensityResponse,
  PeriodSelectorSeriesResponse,
  SEUCategory,
  SeuCategory,
  BuildingIndexResponse,
  CooRun,
  CooRunDetail,
  CooEgressDenial,
  CooStopResult,
  CooStatus,
  CooThread,
  CooThreadDetail,
  CooChatEvent,
  CooFile,
  CooConfirmation,
  CooAutomation,
  CooAutomationInput,
  CooAutomationOptions,
} from '../types';
import { CooChatError, parseSseBuffer } from '@/lib/coo-chat-stream';
import type {
  ConnectorInfo,
  OptionItem as ConnectorOptionItem,
  ValidateResult as ConnectorValidateResult,
  ConnectionOut,
  ConnectionCreateBody,
  IdentityOut as ConnectorIdentity,
  IdentityStatus as ConnectorIdentityStatus,
  Page as ConnectorPage,
  DiscoverStarted,
  DiscoverRunOut,
  LinkItemRequest as ConnectorLinkItemRequest,
  LinkResponse as ConnectorLinkResponse,
  SyncRequestBody,
  SyncJob as ConnectorSyncJob,
  SyncStatusOut,
  ConsentRequestCreate,
  ConsentRequestCreated,
  ConsentRequestOut,
  PlatformCredentialOut,
  AuthorizationOut,
} from '../types/connectors';
import type {
  GridResponse,
  CellWriteRequest,
  CellWriteResponse,
  CellHistoryEntry,
  BulkPasteRequest,
  BulkPasteResponse,
} from '../types/manualEntry';

// 'same-origin' (the NomOS host builds): the legacy root is the page's own origin, so a method NomOS
// does not implement reaches that host's JSON 404 (deploy/login/nginx.conf), never another host.
const API_BASE_URL = import.meta.env.VITE_API_URL === 'same-origin' ? '' : (import.meta.env.VITE_API_URL || 'http://localhost:8000');

class ApiService {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: `${API_BASE_URL}/api/v1`,
      headers: {
        'Content-Type': 'application/json',
      },
      // Serialize arrays as repeated params for FastAPI (device_ids=x&device_ids=y)
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((v) => searchParams.append(key, v));
          } else if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        });
        return searchParams.toString();
      },
    });

    // Load token from localStorage
    this.token = localStorage.getItem('access_token');

    // Request interceptor - add auth header
    this.client.interceptors.request.use((config) => {
      if (this.token) {
        config.headers.Authorization = `Bearer ${this.token}`;
      }
      return config;
    });

    // Response interceptor - handle 401
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) this.handleUnauthorized();
        return Promise.reject(error);
      }
    );
  }

  // A 401 from any request (axios or the chat's streaming fetch): log out and go back to the start.
  private handleUnauthorized(): void {
    // Only reload if we actually had a token (prevents loop on login page)
    const hadToken = !!this.token || nomosTokenPresent();
    this.logout();
    dropActiveSession(); // TZ-2 / B7: forget only the rejected tenant session; other tenants stay signed in
    // Reload to trigger auth check in App component, but only once
    if (hadToken && !window.location.pathname.includes('/login')) {
      window.location.href = '/';
    }
  }

  // Auth
  // `tenantSlug` is used by the NomOS implementation only (B7: one session per tenant); Sentinel ignores it.
  async login(email: string, password: string, _tenantSlug?: string): Promise<TokenResponse> {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);

    const response = await this.client.post<TokenResponse>('/auth/token', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    this.token = response.data.access_token;
    localStorage.setItem('access_token', this.token);
    this.sessionChanged();
    return response.data;
  }

  // TZ-2: the second step of a NomOS login whose password matched in several organisations
  // (the login page's picker). Sentinel has one organisation per account: never reached there.
  async chooseTenant(_token: string, _tenantSlug: string): Promise<TokenResponse> {
    throw new Error('choosing an organisation needs NomOS');
  }

  logout(): void {
    this.token = null;
    localStorage.removeItem('access_token');
    this.sessionChanged();
  }

  // Per-account state kept in the browser (caches, customer settings) must not
  // outlive the account it was loaded for. `sessionEpoch` changes on every login
  // and logout; listeners registered with onSessionChange run after each change.
  private _sessionEpoch = 0;
  private sessionListeners = new Set<() => void>();

  get sessionEpoch(): number {
    return this._sessionEpoch;
  }

  onSessionChange(listener: () => void): () => void {
    this.sessionListeners.add(listener);
    return () => {
      this.sessionListeners.delete(listener);
    };
  }

  private sessionChanged(): void {
    this._sessionEpoch += 1;
    this.sessionListeners.forEach((listener) => listener());
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get<User>('/auth/me');
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.client.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }

  // Password Reset (public - no auth required)
  async forgotPassword(email: string, _tenantSlug?: string): Promise<void> {
    await this.client.post('/auth/forgot-password', { email });
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await this.client.post('/auth/reset-password', {
      token,
      new_password: newPassword,
    });
  }

  // Invitations (public - no auth required)
  async validateInvitation(token: string): Promise<{
    valid: boolean;
    email?: string;
    role_name?: string;
    invited_by_name?: string;
    expires_at?: string;
    error?: string;
  }> {
    const response = await this.client.get(`/auth/invitations/${token}`);
    return response.data;
  }

  async acceptInvitation(token: string, data: {
    password: string;
    first_name: string;
    last_name: string;
    phone?: string;
  }): Promise<{ message: string; user_id: string }> {
    const response = await this.client.post(`/auth/invitations/${token}/accept`, data);
    return response.data;
  }

  isAuthenticated(): boolean {
    // TZ-2 seam: a NomOS session (localStorage nomos_token) counts as authenticated too.
    return !!this.token || nomosTokenPresent();
  }

  // Buildings
  async getBuildingIndex(): Promise<BuildingIndexResponse> {
    const response = await this.client.get<BuildingIndexResponse>('/buildings/index');
    return response.data;
  }

  async getBuildings(params?: {
    page?: number;
    page_size?: number;
    limit?: number;
    search?: string;
    country?: string;
    building_type?: string;
    active_only?: boolean;
    start_date?: string;
    end_date?: string;
    sort?: string;
    order?: 'asc' | 'desc';
  }): Promise<BuildingListResponse> {
    // Map limit to page_size if provided
    const apiParams = params ? {
      ...params,
      page_size: params.limit || params.page_size,
    } : undefined;

    const response = await this.client.get<BuildingListResponse>('/buildings', { params: apiParams });
    const data = response.data;

    // Add pagination object for convenience
    return {
      ...data,
      pagination: {
        total: data.total,
        page: data.page,
        page_size: data.page_size,
        total_pages: data.total_pages,
      },
    };
  }

  async getBuilding(id: string): Promise<Building> {
    const response = await this.client.get<Building>(`/buildings/${id}`);
    return response.data;
  }

  async updateBuilding(id: string, data: BuildingUpdate): Promise<Building> {
    const response = await this.client.put<Building>(`/buildings/${id}`, data);
    return response.data;
  }

  async deleteBuilding(
    id: string,
    options?: {
      delete_device_mappings?: boolean;
      delete_energy_data?: boolean;
      delete_initiatives?: boolean;
    }
  ): Promise<void> {
    await this.client.delete(`/buildings/${id}`, {
      params: options || {},
    });
  }

  async getBuildingDeletionSummary(id: string): Promise<{
    building_id: string;
    building_name: string;
    device_count: number;
    energy_readings_count: number;
    initiatives_count: number;
    has_shelly_connection: boolean;
  }> {
    const response = await this.client.get(`/buildings/${id}/deletion-summary`);
    return response.data;
  }

  async getSEUCategories(): Promise<SEUCategory[]> {
    const response = await this.client.get<SEUCategory[]>('/buildings/seu-categories');
    return response.data;
  }

  // Weather
  // `_opts.priority` is a NomOS transport hint (the read the person waits on goes first in its batch); the retired
  // Sentinel route has no batch and ignores it.
  async getWeatherHistory(buildingId: string, params?: {
    start_date?: string;
    end_date?: string;
    aggregation?: 'hourly' | 'daily';
  }, _opts?: { priority?: ReadPriority }): Promise<WeatherHistoryResponse> {
    const response = await this.client.get<WeatherHistoryResponse>(
      `/weather/${buildingId}/history`,
      { params }
    );
    return response.data;
  }

  async getKPIs(params: {
    building_ids?: string[];
    start_date?: string;
    end_date?: string;
    include_comparison?: boolean;
    /**
     * SEN-1441 — restrict to automatically metered data sources (the monthly
     * retail report's universe). Omit / false for all meters.
     */
    metered_only?: boolean;
    /** Called as each headline KPI settles (NomOS); the legacy endpoint answers once and never calls it. */
    onPartial?: (partial: KPIResponse) => void;
  }): Promise<KPIResponse> {
    const { onPartial: _onPartial, ...query } = params;
    const response = await this.client.get<KPIResponse>('/analytics/kpis', { params: query });
    return response.data;
  }

  // ================================
  // Data quality (FO-2) and compliance — projections of the one quality service
  // ================================

  async getQualityMetrics(params: QualityMetricsParams): Promise<QualityMetricsResponse> {
    const response = await this.client.get<QualityMetricsResponse>('/quality-metrics', { params });
    return response.data;
  }

  async exportQualityFixList(format: FixListFormat, countryCode?: string, gap?: string): Promise<{ blob: Blob; filename: string }> {
    const response = await this.client.get('/quality-metrics/fix-list/export', {
      params: { format, ...(countryCode ? { country_code: countryCode } : {}), ...(gap ? { gap } : {}) },
      responseType: 'blob',
    });
    const disposition: string = response.headers['content-disposition'] ?? '';
    const match = disposition.match(/filename=([^\s;]+)/);
    return { blob: response.data, filename: match ? match[1] : `data-quality-fix-list.${format}` };
  }

  async proposeLegalEntities(dryRun: boolean): Promise<ProposeResult> {
    const response = await this.client.post<ProposeResult>('/compliance/legal-entities/propose', null, {
      params: { dry_run: dryRun },
    });
    return response.data;
  }

  async confirmLegalEntityBuildings(legalEntityId: string, dryRun: boolean): Promise<ConfirmResult> {
    const response = await this.client.post<ConfirmResult>(`/compliance/legal-entities/${legalEntityId}/confirm`, null, {
      params: { dry_run: dryRun },
    });
    return response.data;
  }

  async assignBuildingsToLegalEntity(legalEntityId: string, body: AssignBody, dryRun: boolean): Promise<AssignResult> {
    const response = await this.client.post<AssignResult>(`/compliance/legal-entities/${legalEntityId}/buildings`, body, {
      params: { dry_run: dryRun },
    });
    return response.data;
  }

  async getResponsibleCandidates(): Promise<ResponsibleCandidate[]> {
    const response = await this.client.get<ResponsibleCandidate[]>('/compliance/responsible-candidates');
    return response.data;
  }

  async setCountryResponsible(countryCode: string, body: ResponsibleBody, dryRun: boolean): Promise<CountryResponsibleResult> {
    const response = await this.client.put<CountryResponsibleResult>(
      `/compliance/countries/${encodeURIComponent(countryCode)}/responsible`, body, { params: { dry_run: dryRun } });
    return response.data;
  }

  // ================================
  // Weekly status email (FO-10, SEN-302): same numbers as the Progress tab, via
  // country_progress_service — no second computation path.
  // ================================

  async getWeeklyStatusEmailSettings(): Promise<WeeklyStatusEmailSettings> {
    const response = await this.client.get<WeeklyStatusEmailSettings>('/weekly-status-email/settings');
    return response.data;
  }

  async updateWeeklyStatusEmailSettings(body: WeeklyStatusEmailSettingsUpdate): Promise<WeeklyStatusEmailSettings> {
    const response = await this.client.put<WeeklyStatusEmailSettings>('/weekly-status-email/settings', body);
    return response.data;
  }

  async previewWeeklyStatusEmail(): Promise<WeeklyStatusEmailPreview> {
    const response = await this.client.get<WeeklyStatusEmailPreview>('/weekly-status-email/preview');
    return response.data;
  }

  async sendWeeklyStatusEmailPreview(): Promise<{ queued: boolean; to: string }> {
    const response = await this.client.post<{ queued: boolean; to: string }>('/weekly-status-email/send-preview');
    return response.data;
  }

  // ================================
  // Settings / Configuration APIs
  // ================================

  // Energy Sources (read-only reference data — used by editable meters table
  // picker and any other consumer that needs the lookup list).
  async getEnergySources(): Promise<EnergySource[]> {
    const response = await this.client.get<EnergySource[]>('/energy-sources');
    return response.data;
  }

  // Emission Factors per Energy Source (CO2 factors for different fuels)
  async getEmissionFactorsBySource(params?: {
    country_code?: string;
    energy_source_id?: string;
  }): Promise<EnergySourceEmissionFactor[]> {
    const response = await this.client.get<EnergySourceEmissionFactor[]>('/settings/emission-factors', { params });
    return response.data;
  }

  // Energy Prices
  async getEnergyPrices(params?: {
    country_code?: string;
    energy_source_id?: string;
  }): Promise<EnergyPrice[]> {
    const response = await this.client.get<EnergyPrice[]>('/settings/energy-prices', { params });
    return response.data;
  }

  async createEnergyPrice(data: EnergyPriceCreate): Promise<EnergyPrice> {
    const response = await this.client.post<EnergyPrice>('/settings/energy-prices', data);
    return response.data;
  }

  async updateEnergyPrice(id: string, data: Partial<EnergyPriceCreate>): Promise<EnergyPrice> {
    const response = await this.client.put<EnergyPrice>(`/settings/energy-prices/${id}`, data);
    return response.data;
  }

  async deleteEnergyPrice(id: string): Promise<void> {
    await this.client.delete(`/settings/energy-prices/${id}`);
  }

  // Savings Initiatives
  async getSavingsInitiatives(buildingId: string, params?: {
    status?: string;
    initiative_type?: string;
  }): Promise<SavingsInitiative[]> {
    const response = await this.client.get<SavingsInitiative[]>(
      `/buildings/${buildingId}/initiatives`,
      { params }
    );
    return response.data;
  }

  // Batch fetch all initiatives across buildings (optimized for Actions page)
  //
  // `counts` and `aggregates` are computed server-side over the FULL filtered
  // set — never derive tab badges or KPI cards from `data`, which is one
  // capped page.
  async getBatchInitiatives(params?: {
    building_ids?: string[];
    country_code?: string;
    status?: string;
    status_filter?: string;
    initiative_type?: string;
    /** Lifecycle bucket: 'archive' also covers superseded_at rows. */
    lifecycle?: InitiativeLifecycleBucket;
    /** Include superseded / cancelled rows. Default false server-side. */
    include_archived?: boolean;
    page?: number;
    page_size?: number;
    /** FO-10 (SEN-302): also return counts_by_country — the same per-bucket counts, one row per
     *  country, over the full filtered set, in one call instead of one call per country_code. */
    by_country?: boolean;
  }): Promise<{
    data: (SavingsInitiative & { buildingId?: string; country_code?: string })[];
    total: number;
    buildings_count: number;
    counts: InitiativeLifecycleCounts;
    aggregates: InitiativeAggregates;
    counts_by_country: CountryInitiativeCounts[] | null;
  }> {
    const response = await this.client.get<{
      data: (SavingsInitiative & { building_id: string; country_code: string })[];
      total: number;
      buildings_count: number;
      counts?: InitiativeLifecycleCounts;
      aggregates?: InitiativeAggregates;
      counts_by_country?: CountryInitiativeCounts[] | null;
    }>('/buildings/initiatives/batch', { params });

    // Map building_id to buildingId for frontend compatibility
    return {
      ...response.data,
      counts: response.data.counts ?? EMPTY_INITIATIVE_COUNTS,
      aggregates: response.data.aggregates ?? EMPTY_INITIATIVE_AGGREGATES,
      counts_by_country: response.data.counts_by_country ?? null,
      data: response.data.data.map(item => ({
        ...item,
        buildingId: item.building_id,
      })),
    };
  }

  async createSavingsInitiative(buildingId: string, data: SavingsInitiativeCreate): Promise<SavingsInitiative> {
    const response = await this.client.post<SavingsInitiative>(
      `/buildings/${buildingId}/initiatives`,
      data
    );
    return response.data;
  }

  async updateSavingsInitiative(initiativeId: string, data: Partial<SavingsInitiativeCreate>): Promise<SavingsInitiative> {
    const response = await this.client.put<SavingsInitiative>(`/buildings/initiatives/${initiativeId}`, data);
    return response.data;
  }

  async deleteSavingsInitiative(initiativeId: string): Promise<void> {
    await this.client.delete(`/buildings/initiatives/${initiativeId}`);
  }

  async getEvidenceDoc(path: string): Promise<{ path: string; content: string }> {
    const response = await this.client.get<{ path: string; content: string }>(`/savings/evidence/${path}`);
    return response.data;
  }

  // Consumption Breakdown - shows meter points total + device breakdown
  async getConsumptionBreakdown(params?: {
    building_ids?: string[];
    start_date?: string;
    end_date?: string;
  }): Promise<ConsumptionBreakdownResponse> {
    const response = await this.client.get<ConsumptionBreakdownResponse>(
      '/analytics/consumption-breakdown',
      { params }
    );
    return response.data;
  }

  // SEN-401: period-selector brush data
  async getPeriodSelectorSeries(
    params: { buildingId?: string } = {},
  ): Promise<PeriodSelectorSeriesResponse> {
    const search = new URLSearchParams()
    if (params.buildingId) search.set('building_id', params.buildingId)
    const qs = search.toString()
    const endpoint = qs
      ? `/analytics/period-selector-series?${qs}`
      : '/analytics/period-selector-series'
    const response = await this.client.get<PeriodSelectorSeriesResponse>(endpoint)
    return response.data
  }

  // ================================
  // Users (the name directory). Managing members, invitations and roles is NomOS records and verbs
  // (pages/users/UsersHost.tsx, SEN-438), not this client.
  // ================================

  async getUsers(params?: {
    role?: string;
    is_active?: boolean;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<UserListItem[]> {
    const response = await this.client.get<UserListItem[]>('/users', { params });
    return response.data;
  }

  /** The people a task can be assigned to. Sentinel: its users (every assignee is a user there); NomOS overrides
   *  this with its Person records, logins or not (impl/fieldops.ts getAssignees). */
  async getAssignees(): Promise<AssigneeOption[]> {
    return (await this.getUsers()).map((u) => ({ id: u.id, display_name: u.display_name }));
  }

  // ================================
  // Meter Points & Data Sources APIs
  // ================================

  async getMeterPoints(buildingId: string, includeInactive = false): Promise<MeterPointListResponse> {
    const response = await this.client.get<MeterPointListResponse>(
      `/buildings/${buildingId}/meter-points`,
      { params: { include_inactive: includeInactive } }
    );
    return response.data;
  }

  async getAllMeterPoints(params?: {
    include_inactive?: boolean;
    building_id?: string;
  }): Promise<BulkMeterPointListResponse> {
    const response = await this.client.get<BulkMeterPointListResponse>(
      '/meter-points',
      { params }
    );
    return response.data;
  }

  async updateMeterPoint(meterPointId: string, data: MeterPointUpdate): Promise<MeterPoint> {
    const response = await this.client.put<MeterPoint>(`/meter-points/${meterPointId}`, data);
    return response.data;
  }

  // Lookup endpoint for editable meters table SEU picker.
  // (Energy source picker uses the existing getEnergySources() further up;
  // its path is corrected from /settings/energy-sources to /energy-sources
  // as part of this change.)
  async getSeuCategories(): Promise<SeuCategory[]> {
    const response = await this.client.get<SeuCategory[]>('/seu-categories');
    return response.data;
  }

  // ================================
  // AI Reports APIs
  // ================================

  // ============================================
  // Planned buildings
  // ============================================

  async estimateConsumption(data: ConsumptionEstimateRequest): Promise<ConsumptionEstimate> {
    const response = await this.client.post<ConsumptionEstimate>('/plan/planned-buildings/estimate', data);
    return response.data;
  }

  // ============================================================================
  // Climate Data API
  // ============================================================================

  async getClimateZones(): Promise<ClimateZone[]> {
    const response = await this.client.get<ClimateZone[]>('/climate/zones');
    return response.data;
  }

  async lookupClimateZone(latitude: number, longitude: number): Promise<{
    latitude: number;
    longitude: number;
    climate_zone: ClimateZone | null;
    message?: string;
  }> {
    const response = await this.client.get('/climate/lookup', {
      params: { latitude, longitude },
    });
    return response.data;
  }

  async getEnergyBenchmarks(): Promise<EnergyBenchmark[]> {
    const response = await this.client.get<EnergyBenchmark[]>('/climate/benchmarks');
    return response.data;
  }

  async getBuildingTypeProfiles(): Promise<BuildingTypeProfile[]> {
    const response = await this.client.get<BuildingTypeProfile[]>('/settings/building-type-profiles');
    return response.data;
  }

  async updateBuildingTypeProfile(
    buildingType: string,
    data: { ramp_up_hours: number; ramp_down_hours: number },
  ): Promise<BuildingTypeProfile> {
    const response = await this.client.put<BuildingTypeProfile>(
      `/settings/building-type-profiles/${buildingType}`,
      data,
    );
    return response.data;
  }

  async getGridEmissionFactors(): Promise<GridEmissionFactor[]> {
    const response = await this.client.get<GridEmissionFactor[]>('/climate/emission-factors');
    return response.data;
  }

  async getClimateDataSummary(): Promise<ClimateDataSummary> {
    const response = await this.client.get<ClimateDataSummary>('/climate/summary');
    return response.data;
  }

  // ==========================================================================
  // INTEGRATIONS
  // ==========================================================================

  // Shelly — one login: create-connection validates and stores the account.
  async createShellyConnectionFromCredentials(request: ShellyCredentialsConnectionCreate): Promise<ShellyConnection> {
    const response = await this.client.post<ShellyConnection>('/integrations/shelly/create-connection', request);
    return response.data;
  }

  // Shelly — units (spec 2026-09-16 §4/§5)
  async discoverShellyUnits(connectionId: string): Promise<ShellyUnitsResponse> {
    const response = await this.client.post<ShellyUnitsResponse>(`/integrations/shelly/${connectionId}/discover`);
    return response.data;
  }

  async getShellyUnits(connectionId: string): Promise<ShellyUnitsResponse> {
    const response = await this.client.get<ShellyUnitsResponse>(`/integrations/shelly/${connectionId}/units`);
    return response.data;
  }

  async linkShellyUnits(connectionId: string, request: ShellyLinkUnitsRequest): Promise<ShellyLinkUnitsResponse> {
    const response = await this.client.post<ShellyLinkUnitsResponse>(`/integrations/shelly/${connectionId}/link`, request);
    return response.data;
  }

  // Shelly - Data Sync (SyncStep)
  async startShellySync(connectionId: string, request?: ShellySyncRequest): Promise<ShellySyncJob> {
    const response = await this.client.post<ShellySyncJob>(
      `/integrations/shelly/${connectionId}/sync`,
      request || {}
    );
    return response.data;
  }

  async getShellyActiveSync(connectionId: string): Promise<ShellySyncJob | null> {
    const response = await this.client.get<ShellySyncJob | null>(`/integrations/shelly/${connectionId}/sync/active`);
    return response.data;
  }

  // ===================================================================
  // ELKRAFT INTEGRATION (Swedish Energy via Kiona EnergiWEB)
  // ===================================================================

  // Elkraft - Validation
  async validateElkraftCredentials(request: ElkraftValidateRequest): Promise<ElkraftValidateResponse> {
    const response = await this.client.post<ElkraftValidateResponse>('/integrations/elkraft/validate', request);
    return response.data;
  }

  // Elkraft - Connection Management
  async createElkraftConnection(request: ElkraftConnectionCreate): Promise<ElkraftConnection> {
    const response = await this.client.post<ElkraftConnection>('/integrations/elkraft', request);
    return response.data;
  }

  async getElkraftConnections(): Promise<ElkraftConnectionListResponse> {
    const response = await this.client.get<ElkraftConnectionListResponse>('/integrations/elkraft');
    return response.data;
  }

  // Elkraft - Discovery & Linking
  async discoverElkraftHierarchy(connectionId: string): Promise<ElkraftDiscoverResponse> {
    const response = await this.client.post<ElkraftDiscoverResponse>(`/integrations/elkraft/${connectionId}/discover`);
    return response.data;
  }

  async getElkraftHierarchy(connectionId: string): Promise<{ hierarchy: ElkraftHierarchyNode[] }> {
    const response = await this.client.get<{ hierarchy: ElkraftHierarchyNode[] }>(`/integrations/elkraft/${connectionId}/hierarchy`);
    return response.data;
  }

  async bulkLinkElkraftBuildings(connectionId: string, request: ElkraftBulkLinkRequest): Promise<ElkraftBulkLinkResponse> {
    const response = await this.client.post<ElkraftBulkLinkResponse>(
      `/integrations/elkraft/${connectionId}/link/bulk`,
      request
    );
    return response.data;
  }

  // Elkraft - Data Sync
  async startElkraftSync(connectionId: string, request?: ElkraftSyncRequest): Promise<ElkraftSyncJob> {
    const response = await this.client.post<ElkraftSyncJob>(
      `/integrations/elkraft/${connectionId}/sync`,
      request || {}
    );
    return response.data;
  }

  async getElkraftActiveSync(connectionId: string): Promise<ElkraftSyncJob | null> {
    const response = await this.client.get<ElkraftSyncJob | null>(`/integrations/elkraft/${connectionId}/sync/active`);
    return response.data;
  }

  // ==========================================================================
  // DATADIS INTEGRATION (Spanish Electricity)
  // DEPRECATED: Datadis runs on the unified connector API (/connectors/datadis,
  // the Connector* methods below) and the shared ConnectorWizard. These
  // /integrations/datadis methods go with the old wizard in the Datadis "b" PR,
  // once the owner's prod acceptance has passed.
  // ==========================================================================

  // DataDis - Validation
  async validateDatadisCredentials(request: DatadisValidateRequest): Promise<DatadisValidateResponse> {
    const response = await this.client.post<DatadisValidateResponse>('/integrations/datadis/validate', request);
    return response.data;
  }

  // DataDis - Connection Management
  async createDatadisConnection(request: DatadisConnectionCreate): Promise<DatadisConnectionResponse> {
    const response = await this.client.post<DatadisConnectionResponse>('/integrations/datadis', request);
    return response.data;
  }

  // DataDis - Discovery & Linking
  async discoverDatadisCUPS(connectionId: string): Promise<DatadisDiscoverResponse> {
    const response = await this.client.post<DatadisDiscoverResponse>(`/integrations/datadis/${connectionId}/discover`);
    return response.data;
  }

  async linkDatadisCUPS(connectionId: string, request: DatadisLinkRequest): Promise<MeterLinkResult> {
    const response = await this.client.post<MeterLinkResult>(
      `/integrations/datadis/${connectionId}/link`,
      request
    );
    return response.data;
  }

  async bulkLinkDatadisCUPS(connectionId: string, request: DatadisBulkLinkRequest): Promise<DatadisBulkLinkResponse> {
    const response = await this.client.post<DatadisBulkLinkResponse>(
      `/integrations/datadis/${connectionId}/link/bulk`,
      request
    );
    return response.data;
  }

  // DataDis - Sync
  async startDatadisSync(connectionId: string, request?: DatadisSyncRequest): Promise<DatadisSyncJob> {
    const response = await this.client.post<DatadisSyncJob>(
      `/integrations/datadis/${connectionId}/sync`,
      request || {}
    );
    return response.data;
  }

  async getDatadisActiveSync(connectionId: string): Promise<DatadisSyncJob | null> {
    const response = await this.client.get<DatadisSyncJob | null>(`/integrations/datadis/${connectionId}/sync/active`);
    return response.data;
  }

  // ===== eForsyning (Danish District Heating) =====

  async getEforsyningUtilities(): Promise<EforsyningUtilitiesResponse> {
    const response = await this.client.get<EforsyningUtilitiesResponse>('/integrations/eforsyning/utilities');
    return response.data;
  }

  async validateEforsyningCredentials(request: {
    supplier_slug: string;
    consumer_number: string;
    pin_code: string;
    api_server_url?: string;
  }): Promise<EforsyningValidateResponse> {
    const response = await this.client.post<EforsyningValidateResponse>('/integrations/eforsyning/validate', request);
    return response.data;
  }

  async createEforsyningConnection(request: EforsyningConnectionCreate): Promise<EforsyningConnectionResponse> {
    const response = await this.client.post<EforsyningConnectionResponse>('/integrations/eforsyning', request);
    return response.data;
  }

  async discoverEforsyningMeters(connectionId: string): Promise<EforsyningDiscoverResponse> {
    const response = await this.client.post<EforsyningDiscoverResponse>(`/integrations/eforsyning/${connectionId}/discover`);
    return response.data;
  }

  async linkEforsyningMeter(connectionId: string, request: EforsyningLinkRequest): Promise<MeterLinkResult> {
    const response = await this.client.post<MeterLinkResult>(
      `/integrations/eforsyning/${connectionId}/link`,
      request,
    );
    return response.data;
  }

  async startEforsyningSync(connectionId: string, request?: EforsyningSyncRequest): Promise<EforsyningSyncJob> {
    const response = await this.client.post<EforsyningSyncJob>(
      `/integrations/eforsyning/${connectionId}/sync`,
      request || {},
    );
    return response.data;
  }

  async getEforsyningActiveSync(connectionId: string): Promise<EforsyningSyncJob | null> {
    const response = await this.client.get<EforsyningSyncJob | null>(`/integrations/eforsyning/${connectionId}/sync/active`);
    return response.data;
  }

  // ===== Noova API (Norwegian electricity) =====

  async validateNoovaCredentials(request: { username: string; password: string; customer_id: string }): Promise<NoovaValidateResponse> {
    const response = await this.client.post<NoovaValidateResponse>('/integrations/noova/validate', request);
    return response.data;
  }

  async createNoovaConnection(request: NoovaConnectionCreate): Promise<NoovaConnectionResponse> {
    const response = await this.client.post<NoovaConnectionResponse>('/integrations/noova', request);
    return response.data;
  }

  async updateNoovaCredentials(connectionId: string, request: { username: string; password: string }): Promise<void> {
    await this.client.patch(`/integrations/noova/${connectionId}/credentials`, request);
  }

  // Reconnect: store re-entered credentials on an existing connection and
  // set it active again (audit C1 — errored / reauth_required rows).
  async updateElkraftCredentials(connectionId: string, request: { username: string; password: string }): Promise<void> {
    await this.client.patch(`/integrations/elkraft/${connectionId}/credentials`, request);
  }

  async updateDatadisCredentials(connectionId: string, request: { username: string; password: string }): Promise<void> {
    await this.client.patch(`/integrations/datadis/${connectionId}/credentials`, request);
  }

  async updateEforsyningCredentials(connectionId: string, request: { pin_code: string }): Promise<void> {
    await this.client.patch(`/integrations/eforsyning/${connectionId}/credentials`, request);
  }

  // ================================
  // Unified connectors (/connectors) — one API for every connector, used by
  // the shared ConnectorWizard. Errors are {detail, code}.
  // ================================

  async getConnectors(): Promise<ConnectorInfo[]> {
    const response = await this.client.get<ConnectorInfo[]>('/connectors');
    return response.data;
  }

  async getConnectorOptions(connector: string, field: string): Promise<ConnectorOptionItem[]> {
    const response = await this.client.get<ConnectorOptionItem[]>(`/connectors/${connector}/options/${field}`);
    return response.data;
  }

  async validateConnectorCredentials(connector: string, credentials: Record<string, unknown>): Promise<ConnectorValidateResult> {
    const response = await this.client.post<ConnectorValidateResult>(`/connectors/${connector}/validate`, { credentials });
    return response.data;
  }

  async createConnectorConnection(connector: string, body: ConnectionCreateBody): Promise<ConnectionOut> {
    const response = await this.client.post<ConnectionOut>(`/connectors/${connector}/connections`, body);
    return response.data;
  }

  async getConnectorConnection(connector: string, connectionId: string): Promise<ConnectionOut> {
    const response = await this.client.get<ConnectionOut>(`/connectors/${connector}/connections/${connectionId}`);
    return response.data;
  }

  /** Change a connection's display name; returns it, freshly read. */
  async renameConnectorConnection(connector: string, connectionId: string, name: string): Promise<ConnectionOut> {
    const response = await this.client.patch<ConnectionOut>(
      `/connectors/${connector}/connections/${connectionId}`, { name },
    );
    return response.data;
  }

  /** Stored logins of a connector, no secrets; `status: 'archived'` finds deleted connections' logins. */
  async listConnectorIdentities(
    connector: string, params?: { status?: ConnectorIdentityStatus; limit?: number; cursor?: string },
  ): Promise<ConnectorPage<ConnectorIdentity>> {
    const response = await this.client.get<ConnectorPage<ConnectorIdentity>>(`/connectors/${connector}/identities`, { params });
    return response.data;
  }

  /** Recreate a deleted connection from its archived login — stored credentials, no secret needed. */
  async restoreConnectorConnection(connector: string, identityId: string): Promise<ConnectionOut> {
    return this.createConnectorConnection(connector, { identity_id: identityId });
  }

  async updateConnectorCredentials(connector: string, connectionId: string, credentials: Record<string, unknown>): Promise<void> {
    await this.client.patch(`/connectors/${connector}/connections/${connectionId}/credentials`, { credentials });
  }

  // ---- Consent requests (connectors with a consent link, e.g. Eloverblik) ----

  async createConsentRequest(connector: string, body: ConsentRequestCreate): Promise<ConsentRequestCreated> {
    const response = await this.client.post<ConsentRequestCreated>(`/connectors/${connector}/consent-requests`, body);
    return response.data;
  }

  async getConsentRequest(connector: string, requestId: string): Promise<ConsentRequestOut> {
    const response = await this.client.get<ConsentRequestOut>(`/connectors/${connector}/consent-requests/${requestId}`);
    return response.data;
  }

  async discardConsentRequest(connector: string, requestId: string): Promise<ConsentRequestOut> {
    const response = await this.client.delete<ConsentRequestOut>(`/connectors/${connector}/consent-requests/${requestId}`);
    return response.data;
  }

  // ---- Platform admin: Sentinel's provider credential and the authorizations inbox ----

  async getPlatformCredential(connector: string): Promise<PlatformCredentialOut> {
    const response = await this.client.get<PlatformCredentialOut>(`/connectors/${connector}/platform-credential`);
    return response.data;
  }

  async setPlatformCredential(connector: string, refreshToken: string): Promise<PlatformCredentialOut> {
    const response = await this.client.put<PlatformCredentialOut>(
      `/connectors/${connector}/platform-credential`, { refresh_token: refreshToken },
    );
    return response.data;
  }

  async listConnectorAuthorizations(connector: string): Promise<AuthorizationOut[]> {
    const response = await this.client.get<ConnectorPage<AuthorizationOut>>(`/connectors/${connector}/authorizations`, {
      params: { limit: 500 },
    });
    return response.data.items;
  }

  async assignConnectorAuthorization(connector: string, authorizationId: string, connectionId: string): Promise<ConsentRequestOut> {
    const response = await this.client.post<ConsentRequestOut>(
      `/connectors/${connector}/authorizations/${authorizationId}/assign`, { connection_id: connectionId },
    );
    return response.data;
  }

  async startConnectorDiscover(connector: string, connectionId: string): Promise<DiscoverStarted> {
    const response = await this.client.post<DiscoverStarted>(`/connectors/${connector}/connections/${connectionId}/discover`);
    return response.data;
  }

  async getConnectorDiscoverRun(
    connector: string, connectionId: string, runId: string,
    params?: { limit?: number; cursor?: string },
  ): Promise<DiscoverRunOut> {
    const response = await this.client.get<DiscoverRunOut>(
      `/connectors/${connector}/connections/${connectionId}/discover/${runId}`, { params },
    );
    return response.data;
  }

  /** The finished run with EVERY item (follows next_cursor at the maximum page size). */
  async getConnectorDiscoverRunAll(connector: string, connectionId: string, runId: string): Promise<DiscoverRunOut> {
    const first = await this.getConnectorDiscoverRun(connector, connectionId, runId, { limit: 1000 });
    if (first.status !== 'completed') return first;
    const items = [...first.items];
    let cursor = first.next_cursor;
    while (cursor) {
      const page = await this.getConnectorDiscoverRun(connector, connectionId, runId, { limit: 1000, cursor });
      items.push(...page.items);
      cursor = page.next_cursor;
    }
    return { ...first, items, next_cursor: null };
  }

  async linkConnectorItems(
    connector: string, connectionId: string, items: ConnectorLinkItemRequest[], dryRun = false,
  ): Promise<ConnectorLinkResponse> {
    const response = await this.client.post<ConnectorLinkResponse>(
      `/connectors/${connector}/connections/${connectionId}/link`, { items },
      { params: { dry_run: dryRun } },
    );
    return response.data;
  }

  async startConnectorSync(connector: string, connectionId: string, body: SyncRequestBody = {}): Promise<ConnectorSyncJob> {
    const response = await this.client.post<ConnectorSyncJob>(`/connectors/${connector}/connections/${connectionId}/sync`, body);
    return response.data;
  }

  async getConnectorSyncStatus(connector: string, connectionId: string, params?: { limit?: number; cursor?: string }): Promise<SyncStatusOut> {
    const response = await this.client.get<SyncStatusOut>(
      `/connectors/${connector}/connections/${connectionId}/sync/status`, { params: { limit: 1000, ...params } },
    );
    return response.data;
  }

  async updateKmdCredentials(connectionId: string, request: { email: string; password: string }): Promise<void> {
    await this.client.patch(`/integrations/kmd-minforsyning/${connectionId}/credentials`, request);
  }

  async discoverNoova(connectionId: string): Promise<NoovaDiscoveryReport> {
    const response = await this.client.post<NoovaDiscoveryReport>(`/integrations/noova/${connectionId}/discover`);
    return response.data;
  }

  async linkNoova(connectionId: string): Promise<NoovaLinkResult> {
    const response = await this.client.post<NoovaLinkResult>(`/integrations/noova/${connectionId}/link`);
    return response.data;
  }

  async startNoovaSync(connectionId: string, request: { start_date?: string; end_date?: string } = {}): Promise<{ started: boolean; start_date: string; end_date: string }> {
    const response = await this.client.post<{ started: boolean; start_date: string; end_date: string }>(
      `/integrations/noova/${connectionId}/sync`,
      request,
    );
    return response.data;
  }

  // ===== KMD MinForsyning API =====

  async getKmdPlants(): Promise<KmdPlantsResponse> {
    const response = await this.client.get<KmdPlantsResponse>('/integrations/kmd-minforsyning/plants');
    return response.data;
  }

  async validateKmdCredentials(request: KmdValidateRequest): Promise<KmdValidateResponse> {
    const response = await this.client.post<KmdValidateResponse>(
      '/integrations/kmd-minforsyning/validate',
      request,
    );
    return response.data;
  }

  async createKmdConnection(request: KmdConnectionCreate): Promise<KmdConnectionResponse> {
    const response = await this.client.post<KmdConnectionResponse>(
      '/integrations/kmd-minforsyning',
      request,
    );
    return response.data;
  }

  async discoverKmdMeters(connectionId: string): Promise<KmdDiscoverResponse> {
    const response = await this.client.post<KmdDiscoverResponse>(
      `/integrations/kmd-minforsyning/${connectionId}/discover`,
    );
    return response.data;
  }

  async linkKmdMeter(connectionId: string, request: KmdLinkRequest): Promise<{ meter_point_id: string }> {
    const response = await this.client.post<{ meter_point_id: string }>(
      `/integrations/kmd-minforsyning/${connectionId}/link`,
      request,
    );
    return response.data;
  }

  async startKmdSync(connectionId: string, request?: KmdSyncRequest): Promise<KmdSyncJob> {
    const response = await this.client.post<KmdSyncJob>(
      `/integrations/kmd-minforsyning/${connectionId}/sync`,
      request || {},
    );
    return response.data;
  }

  async getKmdActiveSync(connectionId: string): Promise<KmdSyncJob | null> {
    const response = await this.client.get<KmdSyncJob | null>(
      `/integrations/kmd-minforsyning/${connectionId}/sync/active`,
    );
    return response.data;
  }

  // ===== Google Places API =====

  async checkPlacesConfig(): Promise<{ configured: boolean }> {
    const response = await this.client.get<{ configured: boolean }>('/places/config');
    return response.data;
  }

  async searchPlaces(
    query: string,
    sessionToken?: string,
    countries?: string
  ): Promise<PlacesAutocompleteResponse> {
    const response = await this.client.get<PlacesAutocompleteResponse>('/places/autocomplete', {
      params: {
        query,
        session_token: sessionToken,
        countries,
      },
    });
    return response.data;
  }

  async getPlaceDetails(
    placeId: string,
    sessionToken?: string
  ): Promise<PlaceDetailsResponse> {
    const response = await this.client.get<PlaceDetailsResponse>(`/places/${placeId}/details`, {
      params: { session_token: sessionToken },
    });
    return response.data;
  }

  // ================================
  // Customer Settings
  // ================================

  async getCustomerSettings(): Promise<CustomerSettings> {
    const response = await this.client.get<CustomerSettings>('/customers/me/settings');
    return response.data;
  }

  async getFiscalYears(): Promise<FiscalYear[]> {
    const response = await this.client.get<FiscalYear[]>('/customers/fiscal-years');
    return response.data;
  }

  // ============================================
  // Savings Potential API (Solar Screening)
  // ============================================

  // Solar Business Cases
  async createSolarBusinessCase(data: import('../types/solar').SolarBusinessCaseCreate): Promise<import('../types/solar').SolarBusinessCase> {
    const response = await this.client.post<import('../types/solar').SolarBusinessCase>(
      '/solar/business-cases',
      data
    );
    return response.data;
  }

  // ============================================================
  // Solar / Roof Analysis
  // ============================================================

  /**
   * Generic GET method for API calls
   */
  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await this.client.get<T>(url, { params });
    return response.data;
  }

  /**
   * Generic POST method for API calls
   */
  async post<T>(url: string, data?: unknown, config?: Record<string, unknown>): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  /**
   * Generic PATCH method for API calls
   */
  async patch<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.patch<T>(url, data);
    return response.data;
  }

  // ================================
  // Asset APIs (FO-3b: /api/v1/assets)
  // ================================

  async getAssetTypes(): Promise<AssetType[]> {
    const response = await this.client.get<AssetType[]>('/assets/types');
    return response.data;
  }

  async getAssets(params: AssetQuery): Promise<AssetListResponse> {
    const response = await this.client.get<AssetListResponse>('/assets', { params });
    return response.data;
  }

  /** Counts per group instead of rows (FO-10, SEN-302: per-country confirmed-vs-predicted on the
   *  progress page) — GET /assets?group_by=... on the list-query standard (at most 2 fields),
   *  same pattern as getBuildingOptions(). */
  async groupAssets(params: { group_by: string[]; filter?: string[]; building_id?: string;
    role?: AssetQuery['role'] }): Promise<GroupCounts> {
    const response = await this.client.get<GroupCounts>('/assets', { params });
    return response.data;
  }

  async confirmAssets(ids: string[]): Promise<{ confirmed: number; already_confirmed: number; dry_run: boolean }> {
    const response = await this.client.post('/assets/confirm', { asset_ids: ids });
    return response.data;
  }

  async importAssets(fileId: string, dryRun: boolean): Promise<AssetImportResult> {
    const response = await this.client.post<AssetImportResult>('/assets/import', { file_id: fileId },
      { params: { dry_run: dryRun } });
    return response.data;
  }

  async getAssetFieldDefinitions(category?: string): Promise<AssetFieldDefinitions> {
    const response = await this.client.get<AssetFieldDefinitions>('/assets/field-definitions',
      { params: category ? { category } : {} });
    return response.data;
  }

  async createAsset(data: AssetCreate): Promise<Asset> {
    const response = await this.client.post<Asset>('/assets', data);
    return response.data;
  }

  async updateAsset(id: string, data: AssetUpdate): Promise<Asset> {
    const response = await this.client.patch<Asset>(`/assets/${id}`, data);
    return response.data;
  }

  async deleteAsset(id: string): Promise<void> {
    await this.client.delete(`/assets/${id}`);
  }

  // ================================
  // Physical Task APIs (FO-4: /api/v1/physical-tasks)
  // ================================

  async listTasks(params: PhysicalTaskQuery): Promise<PhysicalTaskListResponse> {
    const response = await this.client.get<PhysicalTaskListResponse>('/physical-tasks', { params });
    return response.data;
  }

  /** Counts per group instead of rows (FO-10, SEN-302: per-country visits/tasks on the progress
   *  page) — GET /physical-tasks?group_by=... on the list-query standard (at most 2 fields), same
   *  pattern as getBuildingOptions(). */
  async groupTasks(params: { group_by: string[]; filter?: string[] }): Promise<GroupCounts> {
    const response = await this.client.get<GroupCounts>('/physical-tasks', { params });
    return response.data;
  }

  async createTask(data: PhysicalTaskCreate): Promise<PhysicalTask> {
    const response = await this.client.post<PhysicalTask>('/physical-tasks', data);
    return response.data;
  }

  async getTask(id: string): Promise<PhysicalTask> {
    const response = await this.client.get<PhysicalTask>(`/physical-tasks/${id}`);
    return response.data;
  }

  /** The intervention's linked tasks (FO-4 Task 7) — GET /savings/initiatives/{id}/tasks
   *  returns the task array directly, not the paged {data, total} shape listTasks() returns. */

  async updateTask(
    id: string,
    data: Partial<Pick<PhysicalTask, 'status' | 'title' | 'description' | 'assignee_user_id' | 'task_type' | 'priority' | 'due_date'>>
  ): Promise<PhysicalTask> {
    const response = await this.client.patch<PhysicalTask>(`/physical-tasks/${id}`, data);
    return response.data;
  }

  // ================================
  // Physical Task detail + Field-ops records APIs (FO-4 Task 6: /api/v1/field-ops)
  // ================================

  async listComments(targetKind: string, targetId: string): Promise<CommentRecord[]> {
    const response = await this.client.get<CommentRecord[]>('/field-ops/comments', {
      params: { target_kind: targetKind, target_id: targetId },
    });
    return response.data;
  }

  async addComment(data: {
    target_kind: string
    target_id: string
    body: string
    parent_id?: string | null
    mentions?: string[]
  }): Promise<CommentRecord> {
    const response = await this.client.post<CommentRecord>('/field-ops/comments', data);
    return response.data;
  }

  async resolveComment(commentId: string): Promise<CommentRecord> {
    const response = await this.client.post<CommentRecord>(`/field-ops/comments/${commentId}/resolve`);
    return response.data;
  }

  async listAttachments(subjectKind: string, subjectId: string): Promise<AttachmentMeta[]> {
    const response = await this.client.get<AttachmentMeta[]>('/field-ops/attachments', {
      params: { subject_kind: subjectKind, subject_id: subjectId },
    });
    return response.data;
  }

  async addAttachment(subjectKind: string, subjectId: string, file: File): Promise<AttachmentMeta> {
    const form = new FormData();
    form.append('subject_kind', subjectKind);
    form.append('subject_id', subjectId);
    form.append('file', file);
    const response = await this.client.post<AttachmentMeta>('/field-ops/attachments', form, {
      headers: { 'Content-Type': undefined },
    });
    return response.data;
  }

  /** The attachment's bytes, for saveBlob — a plain <a href> would bypass axios's auth interceptor. */
  async downloadAttachment(attachmentId: string): Promise<Blob> {
    const response = await this.client.get(`/field-ops/attachments/${attachmentId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  getAttachmentDownloadUrl(attachmentId: string): string {
    return `${this.client.defaults.baseURL}/field-ops/attachments/${attachmentId}/download`;
  }

  async listRecordLinks(kind: string, recordId: string, rel?: string): Promise<LinkRecord[]> {
    const response = await this.client.get<LinkRecord[]>('/field-ops/links', {
      params: { kind, record_id: recordId, ...(rel ? { rel } : {}) },
    });
    return response.data;
  }

  async deleteRecordLink(rel: string, sourceId: string, targetId: string): Promise<void> {
    await this.client.delete('/field-ops/links', { data: { rel, source_id: sourceId, target_id: targetId } });
  }

  async createRecordLink(rel: string, sourceId: string, targetId: string): Promise<LinkActionResponse> {
    const response = await this.client.post<LinkActionResponse>('/field-ops/links', {
      rel, source_id: sourceId, target_id: targetId,
    });
    return response.data;
  }

  // ============================================
  // Triage + Inbox (FO-6b, SEN-297)
  // ============================================

  async listTriageItems(params?: { filter?: string[]; limit?: number; offset?: number }): Promise<ListPage<TriageItem>> {
    const response = await this.client.get<ListPage<TriageItem>>('/field-ops/triage', { params });
    return response.data;
  }

  async getTriageItem(id: string): Promise<TriageItemDetail> {
    const response = await this.client.get<TriageItemDetail>(`/field-ops/triage/${id}`);
    return response.data;
  }

  async dismissTriageItem(id: string): Promise<TriageItem> {
    const response = await this.client.post<TriageItem>(`/field-ops/triage/${id}/dismiss`);
    return response.data;
  }

  async acceptTriageItem(id: string, data: TriageAcceptRequest): Promise<TriageAcceptResponse> {
    const response = await this.client.post<TriageAcceptResponse>(`/field-ops/triage/${id}/accept`, data);
    return response.data;
  }

  async mergeTriageItem(id: string, targetKind: 'task' | 'intervention', targetId: string): Promise<TriageMergeResponse> {
    const response = await this.client.post<TriageMergeResponse>(`/field-ops/triage/${id}/merge`, {
      target_kind: targetKind, target_id: targetId,
    });
    return response.data;
  }

  async listInbox(params?: { unread_only?: boolean; filter?: string[]; limit?: number; offset?: number }): Promise<ListPage<Notification>> {
    const response = await this.client.get<ListPage<Notification>>('/field-ops/inbox', { params });
    return response.data;
  }

  async markNotificationRead(id: string): Promise<Notification> {
    const response = await this.client.post<Notification>(`/field-ops/inbox/${id}/read`);
    return response.data;
  }

  async markAllNotificationsRead(): Promise<{ updated: number }> {
    const response = await this.client.post<{ updated: number }>('/field-ops/inbox/mark-all-read');
    return response.data;
  }

  // ============================================
  // Field-ops contacts, field links, visits (FO-5 Task 7)
  // ============================================

  async listContacts(params?: { role?: string }): Promise<Contact[]> {
    const response = await this.client.get<Contact[]>('/field-ops/contacts', {
      params: params?.role ? { role: params.role } : undefined,
    });
    return response.data;
  }

  async createContact(data: {
    name: string
    email: string
    phone?: string
    role: string
    company?: string
  }): Promise<Contact> {
    const response = await this.client.post<Contact>('/field-ops/contacts', data);
    return response.data;
  }

  async listFieldLinks(taskId: string): Promise<FieldLink[]> {
    const response = await this.client.get<FieldLink[]>(`/field-ops/tasks/${taskId}/field-links`);
    return response.data;
  }

  async issueFieldLink(taskId: string, contactId: string): Promise<FieldLinkIssued> {
    const response = await this.client.post<FieldLinkIssued>(`/field-ops/tasks/${taskId}/field-links`, {
      contact_id: contactId,
    });
    return response.data;
  }

  async revokeFieldLink(linkId: string): Promise<FieldLink> {
    const response = await this.client.post<FieldLink>(`/field-ops/field-links/${linkId}/revoke`);
    return response.data;
  }

  /** The office's view of a contact's visit; null for the normal "link issued, not opened yet" state. */
  async getTaskVisit(taskId: string, contactId: string): Promise<Visit | null> {
    try {
      const response = await this.client.get<Visit>(`/field-ops/tasks/${taskId}/visits/${contactId}`);
      return response.data;
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 404) return null;
      throw e;
    }
  }

  // ============================================
  // Savings Lifecycle
  // ============================================

  async completeInitiative(
    id: string,
    data: {
      completion_date?: string
      actual_savings_kwh?: number
      actual_savings_eur?: number
      actual_cost_eur?: number
      notes?: string
    }
  ): Promise<SavingsInitiative> {
    const response = await this.client.post<SavingsInitiative>(`/savings/initiatives/${id}/complete`, data);
    return response.data;
  }

  async activateInitiative(
    id: string,
    data: {
      owner_name: string
      owner_email: string
      implementation_date: string
    }
  ): Promise<SavingsInitiative> {
    const response = await this.client.post<SavingsInitiative>(`/savings/initiatives/${id}/activate`, data);
    return response.data;
  }

  async changeInitiativeStatus(
    id: string,
    data: { target_status: 'opportunity' | 'active' | 'completed' | 'verified' | 'cancelled'; reason?: string }
  ): Promise<SavingsInitiative> {
    const response = await this.client.post<SavingsInitiative>(`/savings/initiatives/${id}/change-status`, data);
    return response.data;
  }

  async lockRealization(id: string): Promise<SavingsInitiative> {
    const response = await this.client.post<SavingsInitiative>(`/savings/initiatives/${id}/lock-realization`, {});
    return response.data;
  }

  async recomputeRealization(id: string): Promise<SavingsInitiative> {
    const response = await this.client.post<SavingsInitiative>(`/savings/initiatives/${id}/recompute-realization`, {});
    return response.data;
  }

  async cancelInitiative(id: string, data: { reason?: string }): Promise<SavingsInitiative> {
    const response = await this.client.post<SavingsInitiative>(`/savings/initiatives/${id}/cancel`, data);
    return response.data;
  }

  // ============================================
  // PDF Upload API
  // ============================================

  // ================================
  // Business Case APIs
  // ================================

  // ============================================
  // ENTSO-E Electricity Market
  // ============================================

  async getBiddingZones(): Promise<BiddingZone[]> {
    const response = await this.client.get<BiddingZone[]>('/bidding-zones')
    return response.data
  }

  async getSpotPrices(code: string, startDate: string, endDate: string): Promise<PriceResponse> {
    const response = await this.client.get<PriceResponse>(`/bidding-zones/${code}/prices`, {
      params: { start_date: startDate, end_date: endDate },
    })
    return response.data
  }

  async getCarbonIntensity(code: string, startDate: string, endDate: string): Promise<CarbonIntensityResponse> {
    const response = await this.client.get<CarbonIntensityResponse>(`/bidding-zones/${code}/carbon`, {
      params: { start_date: startDate, end_date: endDate },
    })
    return response.data
  }

  // ============================================
  // Manual Entry (SEN-461)
  // ============================================

  async getManualReadingGrid(fiscalYear: number): Promise<GridResponse> {
    const response = await this.client.get<GridResponse>('/manual-readings/grid', {
      params: { fiscal_year: fiscalYear },
    })
    return response.data
  }

  async writeManualCell(req: CellWriteRequest): Promise<CellWriteResponse> {
    const response = await this.client.post<CellWriteResponse>('/manual-readings/cell', req)
    return response.data
  }

  async getCellHistory(
    meterPointId: string,
    fiscalYear: number,
    monthIndex: number,
  ): Promise<CellHistoryEntry[]> {
    const response = await this.client.get<CellHistoryEntry[]>(
      `/manual-readings/cell/${meterPointId}/${fiscalYear}/${monthIndex}/history`,
    )
    return response.data
  }

  async undoCell(
    meterPointId: string,
    fiscalYear: number,
    monthIndex: number,
  ): Promise<CellWriteResponse> {
    const response = await this.client.post<CellWriteResponse>(
      `/manual-readings/cell/${meterPointId}/${fiscalYear}/${monthIndex}/undo`,
    )
    return response.data
  }

  async applyManualBulkPaste(req: BulkPasteRequest): Promise<BulkPasteResponse> {
    const response = await this.client.post<BulkPasteResponse>('/manual-readings/bulk-paste', req)
    return response.data
  }

  /** Fetch with the bearer token, then save the blob. `exactName`: save as `name`, not the server's filename. */
  private async downloadAuthenticatedFile(path: string, fallbackName: string, exactName = false): Promise<void> {
    const response = await this.client.get(path, { responseType: 'blob' });
    const disposition: string = response.headers['content-disposition'] ?? '';
    const match = disposition.match(/filename=\"?([^\";]+)\"?/);
    const url = URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = !exactName && match ? match[1] : fallbackName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // COO automations (admin-only; see backend/app/routers/coo.py)
  async getCooRuns(params?: { status?: string; job?: string }): Promise<CooRun[]> {
    const response = await this.client.get<CooRun[]>('/coo/runs', { params });
    return response.data;
  }

  async getCooRun(runId: string): Promise<CooRunDetail> {
    const response = await this.client.get<CooRunDetail>(`/coo/runs/${runId}`);
    return response.data;
  }

  async getCooEgressDenials(): Promise<CooEgressDenial[]> {
    const response = await this.client.get<CooEgressDenial[]>('/coo/egress-denials');
    return response.data;
  }

  async stopCooRun(runId: string): Promise<CooStopResult> {
    const response = await this.client.post<CooStopResult>(`/coo/runs/${runId}/stop`);
    return response.data;
  }

  async getCooStatus(): Promise<CooStatus> {
    const response = await this.client.get<CooStatus>('/coo/status');
    return response.data;
  }

  async pauseCoo(): Promise<CooStatus> {
    const response = await this.client.post<CooStatus>('/coo/pause');
    return response.data;
  }

  async resumeCoo(): Promise<CooStatus> {
    const response = await this.client.post<CooStatus>('/coo/resume');
    return response.data;
  }

  // Sentinel automations (facility_manager and above; backend/app/routers/coo_automations.py)
  async listCooAutomations(): Promise<CooAutomation[]> {
    return (await this.client.get<CooAutomation[]>('/coo/automations')).data;
  }

  async getCooAutomation(id: string): Promise<CooAutomation> {
    return (await this.client.get<CooAutomation>(`/coo/automations/${id}`)).data;
  }

  async getCooAutomationOptions(): Promise<CooAutomationOptions> {
    return (await this.client.get<CooAutomationOptions>('/coo/automations/options')).data;
  }

  /** Creates a proposal (off). The person saving it then approves it with approveCooAutomation. */
  async createCooAutomation(input: CooAutomationInput): Promise<CooAutomation> {
    return (await this.client.post<CooAutomation>('/coo/automations', input)).data;
  }

  /** A material edit of an approved automation re-approves it as the caller (backend is_material). */
  async updateCooAutomation(id: string, patch: Partial<CooAutomationInput> & { enabled?: boolean }): Promise<CooAutomation> {
    return (await this.client.patch<CooAutomation>(`/coo/automations/${id}`, patch)).data;
  }

  async approveCooAutomation(id: string): Promise<CooAutomation> {
    return (await this.client.post<CooAutomation>(`/coo/automations/${id}/approve`)).data;
  }

  async deleteCooAutomation(id: string): Promise<void> {
    await this.client.delete(`/coo/automations/${id}`);
  }

  // Sentinel chat (any user of a customer; threads are private — see backend/app/routers/coo_chat.py)
  async listCooThreads(): Promise<CooThread[]> {
    return (await this.client.get<CooThread[]>('/coo/chat/threads')).data;
  }

  async createCooThread(): Promise<CooThread> {
    return (await this.client.post<CooThread>('/coo/chat/threads', {})).data;
  }

  async getCooThread(threadId: string): Promise<CooThreadDetail> {
    return (await this.client.get<CooThreadDetail>(`/coo/chat/threads/${threadId}`)).data;
  }

  async deleteCooThread(threadId: string): Promise<void> {
    await this.client.delete(`/coo/chat/threads/${threadId}`);
  }

  async stopCooChatTurn(threadId: string): Promise<CooStopResult> {
    return (await this.client.post<CooStopResult>(`/coo/chat/threads/${threadId}/stop`)).data;
  }

  /** Upload one file for a chat message (xlsx, xlsm, xls, csv, pdf, docx, txt; at most 20 MB). */
  async uploadCooFile(file: File, signal?: AbortSignal): Promise<CooFile> {
    const form = new FormData();
    form.append('file', file);
    return (await this.client.post<CooFile>('/coo/files', form, {
      headers: { 'Content-Type': undefined }, timeout: 300000, signal,
    })).data;
  }

  async deleteCooFile(fileId: string): Promise<void> {
    await this.client.delete(`/coo/files/${fileId}`);
  }

  /** Save one of the caller's chat files (authenticated fetch -> blob), under its own name. */
  async downloadCooFile(fileId: string, name: string): Promise<void> {
    await this.downloadAuthenticatedFile(`/coo/files/${fileId}/download`, name, true);
  }

  // Confirmation cards are NomOS's (services/nomos/impl/chat.ts): Sentinel's chat never previews a write for the
  // person to apply, so its stored answers carry none and these are never reached on Sentinel.
  async applyCooConfirmation(_confirmationId: string): Promise<CooConfirmation> {
    throw new Error('Confirmation cards need NomOS')
  }

  async dismissCooConfirmation(_confirmationId: string): Promise<CooConfirmation> {
    throw new Error('Confirmation cards need NomOS')
  }

  async undoCooConfirmation(_card: CooConfirmation): Promise<CooConfirmation> {
    throw new Error('Confirmation cards need NomOS')
  }

  async getCooConfirmation(_confirmationId: string): Promise<CooConfirmation> {
    throw new Error('Confirmation cards need NomOS')
  }

  /** Ask Sentinel; `onEvent` gets turn, delta, tool and done events as they arrive. Throws CooChatError on a non-2xx. */
  async streamCooChatMessage(threadId: string, text: string, onEvent: (e: CooChatEvent) => void,
                             signal?: AbortSignal, fileIds: string[] = []): Promise<void> {
    // fetch, not axios: axios cannot read a response body while it streams.
    const response = await fetch(`${API_BASE_URL}/api/v1/coo/chat/threads/${threadId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream',
                 ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}) },
      body: JSON.stringify(fileIds.length ? { text, file_ids: fileIds } : { text }),
      signal,
    });
    if (!response.ok) {
      // fetch skips the axios interceptor: an expired session logs out the same way.
      if (response.status === 401) this.handleUnauthorized();
      const body = await response.json().catch(() => ({}));
      throw new CooChatError(response.status, body.code ?? 'error',
                             typeof body.detail === 'string' ? body.detail : `HTTP ${response.status}`);
    }
    if (!response.body) throw new CooChatError(0, 'no_stream', 'Streaming is not supported in this browser');
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const { events, rest } = parseSseBuffer(buffer);
        buffer = rest;
        events.forEach(onEvent);
      }
      const { events } = parseSseBuffer(buffer + decoder.decode() + '\n\n');
      events.forEach(onEvent);
    } finally {
      reader.releaseLock();
    }
  }

}

// Roof Segment Types for map visualization
export interface RoofSegment {
  id: string;
  roof_analysis_id: string;
  segment_index: number;
  area_sqm: number;
  pitch_degrees: number;
  azimuth_degrees: number;
  boundary_coords: { lat: number; lng: number }[] | null;
  annual_sun_hours: number | null;
  panel_count_capacity: number | null;
  is_usable: boolean;
  suitability_score: number | null;
  created_at: string;
}

export interface RoofSegmentListResponse {
  data: RoofSegment[];
  roof_analysis_id: string;
}

// Singleton instance
function nomosTokenPresent(): boolean {
  try { return !!localStorage.getItem('nomos_token'); } catch { return false; }
}

export const api = withNomosSwitch(new ApiService()); // TZ-2 seam: no-op unless VITE_NOMOS_ROUTES / localStorage nomos_routes

export default api;
